// ── CAPR S2 — the APPROVER VIEW (FR-N4 / UX-06–07) ────────────────────────────────────────────
//
// ⚠ PROPOSED AGAINST PRIOR, READ-ONLY. The approver reviews the proposal the assessor submitted —
//   status, rationale, evidence — and nothing on this screen can change it: the server approves the
//   PERSISTED proposal and refuses a body carrying any decision field. Disagreement is a RETURN.
//
// ⚠ CAPR S4 — EACH ITEM'S VERDICT IS THE SERVER'S, shown as sent: "present & integrity-confirmed" or
//   "unverified", with its reasons. No verdict on its own gates the approval; an adverse change since
//   the assessor submitted does, and it is named before the approver tries (the approval would be
//   refused, and the way forward is the existing Return).

import { useMemo, useState } from 'react';
import { CTS_IMPLEMENTATION_STATUS_LABEL, ctsWorkflowLabel } from '@grc/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import type { ControlHub, HubAssignment } from '@/api/types/cts-workspace.types';
import type { EvidenceArtifact } from '@/api/types/evidence-artifacts.types';
import { ControlDefinition, ProposedEvidence, reasonLabel } from './AssessmentContext';
import { priorBasisIds } from './AssessmentForm';
import {
  APPROVE_ACKNOWLEDGE, APPROVE_ACTION, APPROVE_WARNING, statusLabel,
} from './assessment-copy';

const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
const BOX = 'rounded-[8px] border border-border-subtle bg-bg-surface-2 px-[12px] py-[10px]';

export function ApproverReview({ hub, assignment, artifacts, canDecide, busy, error, onApprove, onReturn }: {
  hub: ControlHub;
  assignment: HubAssignment;
  artifacts: EvidenceArtifact[];
  canDecide: boolean;
  busy: boolean;
  error: React.ReactNode;
  onApprove: () => void;
  onReturn: (reason: string) => void;
}) {
  const prior = hub.current_status?.implementation_status ?? null;
  const proposed = assignment.proposed_implementation_status;
  /** CAPR S5 — the server says the approve transaction would refuse THIS viewer (separation of duties). */
  const separationRefusal = (hub.approval_separation?.refusal ?? null) !== null;
  const priorIds = useMemo(() => priorBasisIds(hub), [hub]);
  const [confirming, setConfirming] = useState(false);
  const [ack, setAck] = useState(false);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  const close = () => { setConfirming(false); setAck(false); };

  return (
    <div className="flex flex-col gap-[12px]" data-testid="approver-review">
      <div className={BOX} data-testid="proposed-vs-prior">
        <span className={EYEBROW}>Proposed status</span>
        {proposed ? (
          <p className="mt-[4px] text-[13px] text-text-primary">
            {CTS_IMPLEMENTATION_STATUS_LABEL[proposed]}
            <span className="text-[11.5px] text-text-secondary">
              {prior === null ? ' · no prior status'
                : prior === proposed ? ` · reconfirms the prior status`
                : ` · prior status ${statusLabel(prior)}`}
            </span>
          </p>
        ) : (
          // ⓘ Work submitted before S2 carries no proposal; the server refuses to approve it.
          <p className="mt-[4px] text-[12.5px] text-cert-warn-fg">
            This work was submitted without a proposed status and rationale. Return it so the assessor can record them.
          </p>
        )}
        {assignment.proposed_rationale && (
          <>
            <span className={`${EYEBROW} mt-[8px] block`}>Rationale</span>
            <p className="mt-[3px] whitespace-pre-wrap text-[12.5px] text-text-primary">{assignment.proposed_rationale}</p>
          </>
        )}
        {(assignment.reason_started || assignment.initiation_note) && (
          <p className="mt-[8px] text-[11.5px] text-text-secondary">
            {reasonLabel(assignment.reason_started) ? `Started for: ${reasonLabel(assignment.reason_started)}` : ''}
            {assignment.initiation_note ? `${assignment.reason_started ? ' — ' : ''}${assignment.initiation_note}` : ''}
          </p>
        )}
      </div>

      <ControlDefinition hub={hub} />
      {proposed && (
        <ProposedEvidence selection={assignment.proposed_evidence ?? []} artifacts={artifacts} priorIds={priorIds}
          verification={hub.evidence_verification} />
      )}

      {error && <div className="text-[12px] text-cert-warn-fg" role="alert">{error}</div>}

      {/* ⚠ CAPR S5 — SEPARATION OF DUTIES, STATED BEFORE THE CLICK, IN THE SERVER'S OWN WORDS. The Hub carries
          the approve transaction's answer for THIS viewer (approval_separation); nothing here re-derives it.
          Approve is withheld only when the server says it would refuse; Return is never affected. */}
      {canDecide && separationRefusal && (
        <p className="text-[12px] text-cert-warn-fg" role="note" data-testid="approval-separation-notice">
          {hub.approval_separation!.message}
        </p>
      )}

      {canDecide ? (
        returning ? (
          <div className="flex flex-col gap-[6px]">
            {/* ⚠ The Hub's existing return interaction, unchanged: the MACHINE says this move needs a reason. */}
            <label htmlFor="wf-reason" className="text-[11.5px] text-text-secondary">Why is this being returned?</label>
            <textarea id="wf-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              className="rounded-[7px] border border-border-subtle bg-bg-surface px-[9px] py-[6px] text-[12.5px] text-text-primary" />
            <div className="flex gap-[8px]">
              <Button size="sm" disabled={busy || reason.trim() === ''} onClick={() => onReturn(reason.trim())}
                data-testid="approver-return-confirm">
                Confirm — {ctsWorkflowLabel('returned')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setReturning(false); setReason(''); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-[8px]">
            <Button size="sm" disabled={busy || !proposed || separationRefusal} onClick={() => setConfirming(true)} data-testid="approver-approve-open">
              {APPROVE_ACTION}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setReturning(true)} data-testid="approver-return-open">
              {ctsWorkflowLabel('returned')}
            </Button>
          </div>
        )
      ) : (
        <p className="text-[11.5px] italic text-text-faint" data-testid="awaiting-decision">
          Awaiting a decision by {hub.control.approver_name ?? 'a risk manager'}.
        </p>
      )}

      <Dialog open={confirming} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent data-testid="approver-approve-dialog">
          <DialogHeader>
            <DialogTitle>{APPROVE_ACTION} this assessment?</DialogTitle>
            <DialogDescription>{hub.control.display_code} · {hub.control.title}</DialogDescription>
          </DialogHeader>
          <p className="text-sm">
            Status: <strong>{proposed ? CTS_IMPLEMENTATION_STATUS_LABEL[proposed] : '—'}</strong>
          </p>
          <p className="text-sm">{APPROVE_WARNING}</p>
          <div className="flex items-center gap-2">
            <Checkbox id="approver-approve-ack" checked={ack} onCheckedChange={setAck} />
            <Label htmlFor="approver-approve-ack">{APPROVE_ACKNOWLEDGE}</Label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button data-testid="approver-approve-confirm" disabled={!ack || busy} onClick={() => { close(); onApprove(); }}>
              {busy ? 'Approving…' : APPROVE_ACTION}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
